import React, { useState, useRef, useEffect } from 'react';
import { Button, Divider, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { NavLink, useNavigate } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import { isMobile } from 'react-device-detect';

import { Icons } from '../assets/icons';
import { GuardedComponent } from '../components/guarded-component';
import { useCurrentUser } from '../hooks/use-current-user';
import { UserPermissionName } from '../models/user-permission-name';
import { useAuthorize } from '../hooks/use-authorize';
import { readValue } from '../utils/local-storage.util';
import { StorageKey } from '../constants';
import { usersApiKeyStore } from '../api/users-api-key-store';
import { useLoadData } from '../hooks/useLoadData';
import { ApiResponse } from '../models/api-response';

const isMobileViewEnabled = process.env.REACT_APP_MOBILE_VIEW_FLAG === 'true';

export const MainNavigationPanel = () => {
  const [userName, setUserName] = useState<string>('User');
  const [showMenu, setShowMenu] = useState<boolean>(false);
  const [showSecondMenu, setShowSecondMenu] = useState<boolean>(false);
  const [profileMenu, setProfileMenu] = useState<boolean>(false);
  const [reportsMenu, setReportsMenu] = useState<boolean>(false); // State for reports menu
  const [dropdownStyles, setDropdownStyles] = useState({});
  const navigate = useNavigate();
  const user = useCurrentUser();
  const reportsButtonRef = useRef<HTMLButtonElement>(null);
  const { data, isLoading } = useLoadData<ApiResponse<any>>(usersApiKeyStore.getUserDetails());

  useEffect(() => {
    if (data) {
      localStorage.setItem('USER_NAME', data?.data?.userName);
      setUserName(data?.data?.userName);
    }
  }, [data]);

  const handleHomeNavigation = () => {
    navigate('/');
  };

  const handleClose = (path: string) => {
    setShowMenu(false);
    setShowSecondMenu(false);
    setReportsMenu(false);
    navigate(path);
  };

  const handleProfileClose = (path: string) => {
    setProfileMenu(false);
    navigate(path);
  };

  const channel = new BroadcastChannel('auth');

  const handleLogout = () => {
    channel.postMessage({ type: 'logout' });
    setProfileMenu(false);
    localStorage.clear();
    window.location.href = '/';
  };

  const handleResetPassword = () => {
    setProfileMenu(false);
    navigate('/reset-password');
  };

  const isDisplayProfilePermission = useAuthorize([UserPermissionName?.DISPLAY_PROFILE]);

  const handleEmployeeProfile = () => {
    setProfileMenu(false);
    if (isDisplayProfilePermission) navigate(`/employee/profile/${data?.data?.userID}`);
  };

  const handleReportsMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (reportsButtonRef.current) {
      const rect = reportsButtonRef.current.getBoundingClientRect();
      setDropdownStyles({
        position: 'absolute',
        top: `${rect.bottom - 40}px`,
        left: `${rect.left + 76}px`,
      });
      setReportsMenu(true);
    }
  };

  const handleReportsMenuClose = () => {
    setReportsMenu(false);
  };

  const isLeavePermission = useAuthorize([UserPermissionName?.VIEW_LEAVE]);
  const isMasterPermission = useAuthorize([UserPermissionName?.VIEW_MASTER]);
  const isMarketAreaPermission = useAuthorize([UserPermissionName?.VIEW_MARKET_AREA]);
  const isMunicipalityPermission = useAuthorize([UserPermissionName?.VIEW_MUNICIPALITY]);
  const isServicePermission = useAuthorize([UserPermissionName?.VIEW_SERVICE]);
  const isSkillSetPermission = useAuthorize([UserPermissionName?.VIEW_SKILLSET]);
  const isRolePermission = useAuthorize([UserPermissionName?.VIEW_ROLE]);
  const isEmployeePermission = useAuthorize([UserPermissionName?.VIEW_EMPLOYEE]);
  const isEquipmentPermission = useAuthorize([UserPermissionName?.VIEW_EQUIPMENT]);
  const isDivisionPermission = useAuthorize([UserPermissionName?.VIEW_DIVISION]);
  const isServiceCategoryPermission = useAuthorize([UserPermissionName?.VIEW_SERVICECATEGORY]);
  const isServiceGroupPermission = useAuthorize([UserPermissionName?.VIEW_SERVICEGROUP]);
  const isServiceTypePermission = useAuthorize([UserPermissionName?.VIEW_SERVICETYPE]);
  const isRebateCompanyPermission = useAuthorize([UserPermissionName?.VIEW_REBATECOMPANY]);
  const isProjectSubtypePermission = useAuthorize([UserPermissionName?.VIEW_PROJECTSUBTYPE]);
  const isContactPermission = useAuthorize([UserPermissionName?.VIEW_CONTACT]);
  const isAuditLogPermission = useAuthorize([UserPermissionName?.VIEW_AUDITLOG]);
  const isEmailLogPermission = useAuthorize([UserPermissionName?.VIEW_EMAILLOG]);
  const isWorkOrderReportPermission = useAuthorize([UserPermissionName?.VIEW_WORKORDER_REPORTS]);
  const isInvoiceReportPermission = useAuthorize([UserPermissionName?.VIEW_INVOICE_REPORTS]);

  const isDuxInspectionPermission = useAuthorize([UserPermissionName?.VIEW_DUX_INSPECTION_REQUESTS]);

  const isAllCalendar = useAuthorize([UserPermissionName?.VIEW_ALL_CALENDAR]);
  const isOwnCalendar = useAuthorize([UserPermissionName?.VIEW_OWN_CALENDAR]);
  const isViewTeamCalender = useAuthorize([UserPermissionName.VIEW_TEAM_CALENDAR]);

  const isAllInspection = useAuthorize([UserPermissionName?.VIEW_ALL_INSPECTIONS]);
  const isOwnInspection = useAuthorize([UserPermissionName?.VIEW_OWN_INSPECTIONS]);

  const isHolidayPermission = useAuthorize([UserPermissionName?.VIEW_HOLIDAY]);

  const isPlanEnabled = process.env.REACT_APP_PLAN_FLAG === 'true';
  return (
    <header>
      <nav className="main-Nav">
        <div className="logo" onClick={handleHomeNavigation}>
          <NavLink to="/">
            <img src={Icons.Logo} alt="SI Track" />
          </NavLink>
        </div>
        <div className="navSideTop">
          <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_CUSTOMER]}>
            <div hidden={isMobile && isMobileViewEnabled}>
              <NavLink to="/customers" className="navLink navCustomer">
                <img src={Icons.Cutomer} alt="Customer" />
              </NavLink>
            </div>
          </GuardedComponent>
          <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_OFFICE]}>
            <div hidden={isMobile && isMobileViewEnabled}>
              <NavLink to="/offices" className="navLink navOffices">
                <img src={Icons.OfficeDetails} alt="Office" />
              </NavLink>
            </div>
          </GuardedComponent>
          <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_PROJECT]}>
            <div hidden={isMobile && isMobileViewEnabled}>
              <NavLink to="/projects" className="navLink navProjects">
                <img src={Icons.Project} alt="Projects" />
              </NavLink>
            </div>
          </GuardedComponent>
          <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_UNIT]}>
            <div hidden={isMobile && isMobileViewEnabled}>
              <NavLink to="/units" className="navLink navUnits">
                <img src={Icons.Units} alt="Units" />
              </NavLink>
            </div>
          </GuardedComponent>
          <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_WORKORDER]}>
            <div hidden={isMobile && isMobileViewEnabled}>
              <NavLink to="/workorders" className="navLink navWorkOrders">
                <img src={Icons.workorder} alt="Work Orders" />
              </NavLink>
            </div>
          </GuardedComponent>
          <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_PLAN]}>
            {isPlanEnabled && (
              <div hidden={isMobile && isMobileViewEnabled}>
                <NavLink to="/plan-library" className="navLink navPlanLibrary">
                  <img src={Icons.PlanLibrary} alt="Plan Library" />
                </NavLink>
              </div>
            )}
          </GuardedComponent>
          <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_PLAN_REVIEW_DASHBOARD]}>
            {isPlanEnabled && (
              <div hidden={isMobile && isMobileViewEnabled}>
                <NavLink to="/plan-reviews" className="navLink navPlanReviews">
                  <img src={Icons.PlanReview} alt="Plan Review" />
                </NavLink>
              </div>
            )}
          </GuardedComponent>
          <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_OPERATIONMANAGEMENT]}>
            <div hidden={isMobile && isMobileViewEnabled}>
              <NavLink to="/operation-management" className="navLink navOperationManagement">
                <img src={Icons.OperationManagement} alt="Operation Management" />
              </NavLink>
            </div>
          </GuardedComponent>
          <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_BULKSCHEDULING]}>
            <div hidden={isMobile && isMobileViewEnabled}>
              <NavLink to="/scheduling" className="navLink navBulkScheduling">
                <img src={Icons.bulkSchedule} alt="Bulk Scheduling" />
              </NavLink>
            </div>
          </GuardedComponent>
          {/* <GuardedComponent
            requiredPermissions={[UserPermissionName.VIEW_ALL_CALENDAR || UserPermissionName.VIEW_OWN_CALENDAR]}
          > */}
          {(isAllCalendar || isOwnCalendar || isViewTeamCalender) && (
            <div>
              <NavLink to="/inspector-calendar" className="navLink navScheduleBalance">
                <img src={Icons.IconCalendar} alt="Schedule Balance" />
              </NavLink>
            </div>
          )}
          {/* </GuardedComponent> */}
          {(isWorkOrderReportPermission ||
            isAllInspection ||
            isOwnInspection ||
            isDuxInspectionPermission ||
            isInvoiceReportPermission) && (
            <div hidden={isMobile && isMobileViewEnabled}>
              <Button
                className="navLink navReports"
                id="reports-menu-button"
                aria-controls={reportsMenu ? 'reports-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={reportsMenu ? 'true' : undefined}
                onClick={handleReportsMenuOpen}
                ref={reportsButtonRef}
              >
                <img src={Icons.IconReports} alt="Reports" />
              </Button>
            </div>
          )}

          {reportsMenu && (
            <ul id="reports-menu" className="popupMenu" style={dropdownStyles} onMouseLeave={handleReportsMenuClose}>
              <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_WORKORDER_REPORTS]}>
                <MenuItem
                  onClick={(e) => {
                    handleClose('/workorder-reports/');
                  }}
                >
                  Work Order Report
                </MenuItem>
              </GuardedComponent>
              <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_WORKORDER_REPORT_V2]}>
                <MenuItem
                  onClick={(e) => {
                    handleClose('/workorder-reports-paging/');
                  }}
                >
                  Work Order Report V2
                </MenuItem>
              </GuardedComponent>
              {(isAllInspection || isOwnInspection) && (
                <MenuItem
                  onClick={(e) => {
                    handleClose('/workorder-inspection-report');
                  }}
                >
                  Work Order Inspection Reports
                </MenuItem>
              )}

              {/* <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_WORKORDER_INSPECTION_REPORT]}>
                <MenuItem
                  onClick={(e) => {
                    handleClose('/workorder-inspection-report');
                  }}
                >
                  Work Order Inspection Report
                </MenuItem>
              </GuardedComponent> */}
              <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_DUX_INSPECTION_REQUESTS]}>
                <MenuItem
                  onClick={(e) => {
                    handleClose('/dux-inspection-request');
                  }}
                >
                  DUX Inspection Request
                </MenuItem>
              </GuardedComponent>
              <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_INVOICE_REPORTS]}>
                <MenuItem
                  onClick={() => {
                    handleClose('/invoice-reports');
                  }}
                >
                  Invoice Report
                </MenuItem>
              </GuardedComponent>

              <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_INVOICE_EXCEPTION_REPORTS]}>
                <MenuItem
                  onClick={() => {
                    handleClose('/invoice-exception-report');
                  }}
                >
                  Invoice Exception Report
                </MenuItem>
              </GuardedComponent>
              <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_CUSTOM_REPORT]}>
                <MenuItem
                  onClick={() => {
                    handleClose('/custom-builder');
                  }}
                >
                  Custom Report
                </MenuItem>
              </GuardedComponent>

              <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_COC_REPORT]}>
                <MenuItem
                  onClick={() => {
                    handleClose('/coc');
                  }}
                >
                  CoC Report
                </MenuItem>
              </GuardedComponent>

              <GuardedComponent requiredPermissions={[UserPermissionName.REINSPECTION_EXCEPTION_REPORT]}>
                <MenuItem
                  onClick={() => {
                    handleClose('/reinspection-exception-report');
                  }}
                >
                  Reinspection Exception Report
                </MenuItem>
              </GuardedComponent>
              <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_REQUESTS]}>
                <MenuItem
                  onClick={(e) => {
                    handleClose('/project-request');
                  }}
                >
                  Project Request
                </MenuItem>
              </GuardedComponent>
            </ul>
          )}

          <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_SCHEDULEBALANCING]}>
            <div hidden={isMobile && isMobileViewEnabled}>
              <NavLink to="/schedule-balancing" className="navLink navScheduleBalancing">
                <img src={Icons.IconScheduleBalancing} alt="Schedule Balancing" />
              </NavLink>
            </div>
          </GuardedComponent>

          {/* query builder */}
          {/* <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_PLAN]}> */}
          {/* {isPlanEnabled && ( */}

          {/* )} */}
          {/* </GuardedComponent> */}
        </div>
        <div className="navSideBottom">
          <div>
            <Button>
              <img src={Icons.Bell} alt="Bell" />
            </Button>
          </div>
          <div>
            {(isMasterPermission ||
              isLeavePermission ||
              isMarketAreaPermission ||
              isMunicipalityPermission ||
              isServicePermission ||
              isSkillSetPermission ||
              isRolePermission ||
              isEmployeePermission ||
              isEquipmentPermission ||
              isDivisionPermission ||
              isServiceCategoryPermission ||
              isServiceGroupPermission ||
              isServiceTypePermission ||
              isRebateCompanyPermission ||
              isProjectSubtypePermission ||
              isContactPermission ||
              isAuditLogPermission ||
              isHolidayPermission ||
              isEmailLogPermission) && (
              <div hidden={isMobile && isMobileViewEnabled}>
                <Button
                  id="menu-button"
                  aria-controls={showMenu ? 'demo-positioned-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={showMenu ? 'true' : undefined}
                  onClick={(e) => {
                    setShowMenu(!showMenu);
                  }}
                >
                  <img src={Icons.Tune} alt="Units" />
                </Button>
              </div>
            )}
            <Menu
              elevation={0}
              id="admin-menu"
              className="popupMenu"
              aria-labelledby="menu-button"
              open={showMenu}
              onClose={() => setShowMenu(!showMenu)}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              slotProps={{
                paper: {
                  style: {
                    maxHeight: '80vh', // Adjust the height as needed
                    overflowY: 'auto',
                  },
                },
              }}
            >
              <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_MASTER]}>
                <MenuItem
                  onClick={(e) => {
                    handleClose('/masters');
                  }}
                >
                  Dashboard
                </MenuItem>
              </GuardedComponent>
              <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_AUDITLOG]}>
                <MenuItem
                  onClick={(e) => {
                    handleClose('/audit-log');
                  }}
                >
                  Audit Log
                </MenuItem>{' '}
              </GuardedComponent>
              <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_EMAILLOG]}>
                <MenuItem
                  onClick={(e) => {
                    handleClose('/email-log');
                  }}
                >
                  Email Log
                </MenuItem>{' '}
              </GuardedComponent>
              <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_CONTACT]}>
                <MenuItem
                  onClick={(e) => {
                    handleClose('/masters/contact');
                  }}
                >
                  Contact
                </MenuItem>{' '}
              </GuardedComponent>
              <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_DIVISION]}>
                <MenuItem
                  onClick={(e) => {
                    handleClose('/masters/division');
                  }}
                >
                  Division
                </MenuItem>
              </GuardedComponent>
              <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_DEPARTMENT]}>
                <MenuItem
                  onClick={(e) => {
                    handleClose('/masters/department');
                  }}
                >
                  Department
                </MenuItem>
              </GuardedComponent>
              <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_EMPLOYEE]}>
                <MenuItem
                  onClick={(e) => {
                    handleClose('/masters/employee');
                  }}
                >
                  Employee
                </MenuItem>
              </GuardedComponent>
              <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_EQUIPMENT]}>
                <MenuItem
                  onClick={(e) => {
                    handleClose('/masters/equipment');
                  }}
                >
                  Equipment
                </MenuItem>
              </GuardedComponent>
              <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_LEAVE]}>
                <MenuItem
                  onClick={(e) => {
                    handleClose('/masters/manage-leave');
                  }}
                >
                  Manage Leave
                </MenuItem>
              </GuardedComponent>
              <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_MARKET_AREA]}>
                <MenuItem
                  onClick={(e) => {
                    handleClose('/masters/market-area');
                  }}
                >
                  Market Area
                </MenuItem>
              </GuardedComponent>
              <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_MUNICIPALITY]}>
                <MenuItem
                  onClick={(e) => {
                    handleClose('/masters/municipality');
                  }}
                >
                  Municipality
                </MenuItem>
              </GuardedComponent>
              <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_PROJECTSUBTYPE]}>
                <MenuItem
                  onClick={(e) => {
                    handleClose('/masters/project-subtype');
                  }}
                >
                  Project Subtype
                </MenuItem>{' '}
              </GuardedComponent>
              <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_REBATECOMPANY]}>
                <MenuItem
                  onClick={(e) => {
                    handleClose('/masters/rebate-company');
                  }}
                >
                  Rebate Company
                </MenuItem>{' '}
              </GuardedComponent>
              <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_ROLE]}>
                <MenuItem
                  onClick={(e) => {
                    handleClose('/masters/role');
                  }}
                >
                  Role
                </MenuItem>
              </GuardedComponent>
              <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_SERVICE]}>
                <MenuItem
                  onClick={(e) => {
                    handleClose('/masters/service');
                  }}
                >
                  Service
                </MenuItem>
              </GuardedComponent>
              <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_SERVICECATEGORY]}>
                <MenuItem
                  onClick={(e) => {
                    handleClose('/masters/service-category');
                  }}
                >
                  Service Category
                </MenuItem>
              </GuardedComponent>

              <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_SERVICEGROUP]}>
                <MenuItem
                  onClick={(e) => {
                    handleClose('/masters/service-group');
                  }}
                >
                  Service Group
                </MenuItem>
              </GuardedComponent>
              <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_SERVICETYPE]}>
                <MenuItem
                  onClick={(e) => {
                    handleClose('/masters/service-type');
                  }}
                >
                  Service Type
                </MenuItem>
              </GuardedComponent>

              <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_SKILLSET]}>
                <MenuItem
                  onClick={(e) => {
                    handleClose('/masters/skill-set');
                  }}
                >
                  Skill Set
                </MenuItem>
              </GuardedComponent>
              <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_HOLIDAY]}>
                <MenuItem
                  onClick={(e) => {
                    handleClose('/masters/holiday-management');
                  }}
                >
                  Holiday Management
                </MenuItem>
              </GuardedComponent>
            </Menu>
          </div>
          <div>
            <Button
              onClick={(e) => {
                setProfileMenu(!profileMenu);
              }}
            >
              <img src={Icons.Profile} alt="Profile" />
            </Button>
            <Menu
              elevation={0}
              id="admin-menu"
              className="popupAdminMenu"
              aria-labelledby="menu-button"
              open={profileMenu}
              onClose={() => setProfileMenu(!profileMenu)}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              style={{
                minWidth: `${!userName ? 1 : userName?.length * 10 + 100}px`, // Adjust the calculation as needed
              }}
            >
              <MenuItem
                onClick={() => {
                  handleEmployeeProfile();
                }}
              >
                Hello&nbsp;<strong>{userName}</strong>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleResetPassword();
                }}
              >
                Reset password
              </MenuItem>
              {(!isMobile || !isMobileViewEnabled) && (
                <>
                  <Divider />
                  <MenuItem
                    onClick={(e) => {
                      handleLogout();
                    }}
                  >
                    <ListItemIcon>
                      <PowerSettingsNewIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Logout</ListItemText>
                  </MenuItem>
                </>
              )}
            </Menu>
          </div>
        </div>
      </nav>
    </header>
  );
};
